/**
 * Embed bridge: the CRM running inside the dashboard's CRM frame.
 *
 * The dashboard keeps ONE CRM frame mounted for all its CRM sections and moves
 * it with postMessage instead of a new SSO ticket and a full reload per click
 * (Mineblock-LLC client/src/pages/crm/CrmFrame.jsx). Messages are
 * `{type: 'fos:*'}`:
 *   parent -> CRM  fos:hello               tells us the parent's origin
 *   parent -> CRM  fos:navigate {path}     in-app navigation
 *   CRM -> parent  fos:ready {v, path}     SPA running with a session
 *   CRM -> parent  fos:navigated {path}
 *   CRM -> parent  fos:auth-required {path}  no or expired session: the parent
 *                                            signs in again; we never show the
 *                                            login screen inside the frame
 *
 * Trust: a message is accepted only from `window.parent` and, once known, only
 * from the parent's exact origin. Who may be the parent at all is enforced by
 * the browser through CSP `frame-ancestors`. We only ever post to that exact
 * origin, never '*'. Accepted paths are in-app `/app...` paths only.
 */

export const BRIDGE_VERSION = 1;

let parentOrigin = null;
let pendingAuth = false;

function isPath(path) {
  return typeof path === "string" && /^\/app(?:\/[^?#\\]*)?$/.test(path) && !path.includes("..");
}

/** True when this document is framed (the dashboard's CRM frame or any other allowed ancestor). */
export function isEmbedded() {
  try {
    return typeof window !== "undefined" && window.parent && window.parent !== window;
  } catch {
    return true;
  }
}

function originFromAncestors() {
  try {
    const list = window.location.ancestorOrigins;
    if (list && list.length) return list[0];
  } catch { /* not supported */ }
  try {
    if (document.referrer) {
      const origin = new URL(document.referrer).origin;
      if (origin && origin !== "null" && origin !== window.location.origin) return origin;
    }
  } catch { /* no referrer */ }
  return null;
}

/** Send to the parent at its exact origin; dropped while the origin is unknown. */
export function postToParent(message) {
  if (!isEmbedded()) return false;
  if (!parentOrigin) parentOrigin = originFromAncestors();
  if (!parentOrigin) return false;
  try {
    window.parent.postMessage(message, parentOrigin);
    return true;
  } catch {
    return false;
  }
}

/** The in-app path (after the /<CODE>/crm prefix) of the current URL. */
export function appPath(pathname, prefix) {
  const rest = prefix && pathname.startsWith(prefix) ? pathname.slice(prefix.length) : pathname;
  return rest || "/app";
}

/**
 * Ask the parent for a fresh sign-in. Returns true when a parent will handle
 * it, so callers render a neutral loading state instead of the login page.
 */
export function requestParentSignIn(path) {
  if (!isEmbedded()) return false;
  pendingAuth = true;
  // If the parent's origin is not known yet (no referrer, no ancestorOrigins),
  // the fos:hello sent on the frame's load event answers with auth-required.
  postToParent({ type: "fos:auth-required", path: isPath(path) ? path : "/app" });
  return true;
}

/**
 * Start listening. `navigate(path)` performs an in-app navigation; `current()`
 * returns the in-app path shown; `authState()` is "loading", "yes" or "no".
 * Returns an unsubscribe function.
 */
export function startEmbedBridge({ navigate, current, authState }) {
  if (!isEmbedded()) return () => {};
  function onMessage(event) {
    if (event.source !== window.parent) return;
    const data = event.data;
    if (!data || typeof data !== "object" || typeof data.type !== "string") return;
    if (data.type === "fos:hello") {
      if (!parentOrigin) parentOrigin = event.origin;
      if (event.origin !== parentOrigin) return;
      // While the session check is still running, the answer is sent when it
      // settles (announceReady / requestParentSignIn); the origin is now known.
      const state = authState();
      if (pendingAuth || state === "no") {
        postToParent({ type: "fos:auth-required", path: current() });
      } else if (state === "yes") {
        postToParent({ type: "fos:ready", v: BRIDGE_VERSION, path: current() });
      }
      return;
    }
    if (!parentOrigin || event.origin !== parentOrigin) return;
    if (data.type === "fos:navigate" && isPath(data.path)) {
      navigate(data.path);
    }
  }
  window.addEventListener("message", onMessage);
  return () => window.removeEventListener("message", onMessage);
}

/** Announce readiness (called once the session is known). */
export function announceReady(path) {
  pendingAuth = false;
  postToParent({ type: "fos:ready", v: BRIDGE_VERSION, path });
}

export function announceNavigated(path) {
  if (isPath(path)) postToParent({ type: "fos:navigated", path });
}

// Tests only.
export function _resetEmbedBridgeForTests() {
  parentOrigin = null;
  pendingAuth = false;
}
